import React, { useState, useEffect } from 'react';
import { Smartphone, X } from 'lucide-react';

/**
 * GetAppBanner — dismissible banner prompting tradespeople to install the
 * OnSiteHand PWA. Surfaces on the trade dashboard + chat screen so trades on
 * mobile see it at the most contextually useful moments.
 *
 * Behaviour:
 *  - Hidden once dismissed (persisted in localStorage for 30 days).
 *  - Hidden if app already runs in standalone/PWA mode.
 *  - On Android Chrome/Edge, the "Install app" button triggers the native
 *    beforeinstallprompt. On iOS Safari (no prompt API), we show how-to text.
 */

const DISMISS_KEY = 'osh_get_app_banner_dismissed_at';
const DISMISS_DAYS = 30;

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
}

function wasRecentlyDismissed() {
  try {
    const v = window.localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_DAYS * 86400 * 1000;
  } catch {
    return false;
  }
}

export default function GetAppBanner() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState('other');

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/android/i.test(ua)) setPlatform('android');
    else if (/iphone|ipad|ipod/i.test(ua)) setPlatform('ios');

    setVisible(true);

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!visible) return null;

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (outcome === 'accepted') {
        setVisible(false);
      }
    } else if (platform === 'ios') {
      // Chrome can't trigger a prompt on iOS — open a lightweight info overlay
      alert('On iPhone: tap the Share icon, then "Add to Home Screen".');
    } else {
      // Desktop Chrome / Android without a cached prompt event
      alert('Open this page in Chrome on Android and tap "Add to Home screen" from the menu.');
    }
  };

  const handleDismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  return (
    <div
      className="relative rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-3 sm:p-4 mb-4 flex items-center gap-3"
      data-testid="get-app-banner"
    >
      <div className="flex-shrink-0 p-2 bg-orange-100 rounded-lg">
        <Smartphone className="h-5 w-5 text-orange-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Get the OnSiteHand app</p>
        <p className="text-xs text-slate-600 mt-0.5">Never miss a job opportunity — push alerts the moment an employer messages you.</p>
      </div>
      <button
        type="button"
        onClick={handleInstall}
        className="flex-shrink-0 px-3 py-1.5 bg-orange-600 text-white text-xs font-semibold rounded-lg hover:bg-orange-700 transition-colors"
        data-testid="get-app-banner-install"
      >
        Install
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-700"
        data-testid="get-app-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
